import { ajouterJours, ecartJours, formaterMontant, JOURS, jourDeLaSemaine } from '@admin-pmvb/core'
import { Link } from '@tanstack/react-router'
import type { CSSProperties } from 'react'
import type { Echeance, Paiement, Tache } from '../api'
import { jourCourt, LIBELLES_RETARD, LIBELLES_STATUT_ECHEANCE, signe } from '../libelles'

// Semaine du lundi au dimanche : une colonne par jour (empilées sur téléphone). Les
// échéances à date fixe tombent dans leur jour ; les fenêtres qui touchent la semaine
// sont des bandes au-dessus des jours, tronquées aux bords de la semaine. En surcouche,
// les paiements attendus et les tâches ouvertes à leur date.

const fenetre = (e: Echeance) => e.finFenetre !== null && e.finFenetre > e.date
const dates = (e: Echeance) => `du ${jourCourt(e.date)} au ${jourCourt(e.finFenetre!)}`

export function Semaine({
  lundi,
  aujourdhui,
  echeances,
  retards = new Set(),
  paiements = [],
  taches = [],
}: {
  /** Premier jour de la semaine affichée. */
  lundi: string
  aujourdhui: string
  echeances: Echeance[]
  /** Échéances dont le paiement est en retard (indicateur calculé) : en rouge. */
  retards?: Set<string>
  paiements?: Paiement[]
  taches?: Tache[]
}) {
  const jours = Array.from({ length: 7 }, (_, i) => ajouterJours(lundi, i))
  const dimanche = jours[6]!
  const bandes = echeances.filter((e) => fenetre(e) && e.date <= dimanche && e.finFenetre! >= lundi)
  const classesEcheance = (e: Echeance) =>
    [
      `statut-${e.statut}`,
      e.certitude === 'a_confirmer' && e.statut === 'a_venir' ? 'a-confirmer' : '',
      retards.has(e.id) ? 'en-retard' : '',
    ].join(' ')

  return (
    <div className="semaine">
      <div className="semaine-grille semaine-entete" aria-hidden="true">
        {jours.map((j) => (
          <EnteteJour key={j} jour={j} aujourdhui={aujourdhui} />
        ))}
      </div>

      {bandes.length > 0 && (
        <ul className="semaine-grille semaine-bandes" aria-label="Fenêtres ouvertes cette semaine">
          {bandes.map((e) => {
            const debut = Math.max(0, ecartJours(lundi, e.date))
            const fin = Math.min(6, ecartJours(lundi, e.finFenetre!))
            return (
              <li
                key={e.id}
                style={{ '--debut': debut + 1, '--fin': fin + 2 } as CSSProperties}
                className="semaine-bande-place"
              >
                <Link
                  to="/echeances/$id"
                  params={{ id: e.id }}
                  className={`semaine-bande ${classesEcheance(e)}`}
                  title={`${e.libelle} (${e.periode}), ${dates(e)}`}
                >
                  {e.date < lundi && <span aria-hidden="true">‹ </span>}
                  <strong>{e.libelle}</strong>
                  <span className="discret">
                    {' '}
                    {e.entite} · {dates(e)}
                  </span>
                  {e.finFenetre! > dimanche && <span aria-hidden="true"> ›</span>}
                </Link>
              </li>
            )
          })}
        </ul>
      )}

      <div className="semaine-grille semaine-jours">
        {jours.map((j) => {
          const duJour = echeances.filter((e) => !fenetre(e) && e.date === j)
          const limites = bandes.filter((e) => e.finFenetre === j)
          const paiementsDuJour = paiements.filter((p) => p.datePrevue === j)
          const tachesDuJour = taches.filter((t) => t.dateDue === j)
          const vide =
            duJour.length + limites.length + paiementsDuJour.length + tachesDuJour.length === 0
          return (
            <section
              key={j}
              aria-label={jourCourt(j)}
              className={[
                'semaine-jour',
                j === aujourdhui ? 'aujourdhui' : '',
                [0, 6].includes(jourDeLaSemaine(j)) ? 'week-end' : '',
                vide ? 'sans-rien' : '',
              ].join(' ')}
            >
              <EnteteJour jour={j} aujourdhui={aujourdhui} telephone />
              {duJour.map((e) => (
                <Link
                  key={e.id}
                  to="/echeances/$id"
                  params={{ id: e.id }}
                  className={`semaine-item ${classesEcheance(e)}`}
                >
                  <strong>{e.libelle}</strong>
                  <span className="discret">
                    {[e.entite, e.periode].join(' · ')}
                    {e.montantPrevuCts !== null && (
                      <> · {formaterMontant(e.montantPrevuCts, e.devise)}</>
                    )}
                  </span>
                  <span className="semaine-etat">
                    {e.certitude === 'a_confirmer' && e.statut === 'a_venir'
                      ? 'À confirmer'
                      : LIBELLES_STATUT_ECHEANCE[e.statut]}
                    {retards.has(e.id) && ' · paiement en retard'}
                  </span>
                </Link>
              ))}
              {limites.map((e) => (
                <Link
                  key={e.id}
                  to="/echeances/$id"
                  params={{ id: e.id }}
                  className={`semaine-item ${classesEcheance(e)}`}
                >
                  <strong>{e.libelle}</strong>
                  <span className="discret">
                    {e.entite} · fin de la fenêtre
                    {e.montantPrevuCts !== null && (
                      <> · {formaterMontant(e.montantPrevuCts, e.devise)}</>
                    )}
                  </span>
                  <span className="semaine-etat">{LIBELLES_STATUT_ECHEANCE[e.statut]}</span>
                </Link>
              ))}
              {paiementsDuJour.map((p) => {
                const contenu = (
                  <>
                    <strong>
                      {signe(p.sens)}
                      {formaterMontant(p.resteCts, p.devise)}
                    </strong>
                    <span className="discret">
                      {[p.libelle ?? p.echeance, p.entite].filter(Boolean).join(' · ')}
                    </span>
                    {LIBELLES_RETARD[p.retard] && (
                      <span className="semaine-etat">{LIBELLES_RETARD[p.retard]}</span>
                    )}
                  </>
                )
                const classes = `semaine-item semaine-paiement ${p.retard === 'en_retard' ? 'en-retard' : ''}`
                return p.echeanceId ? (
                  <Link
                    key={p.id}
                    to="/echeances/$id"
                    params={{ id: p.echeanceId }}
                    className={classes}
                  >
                    {contenu}
                  </Link>
                ) : (
                  <Link key={p.id} to="/paiements" className={classes}>
                    {contenu}
                  </Link>
                )
              })}
              {tachesDuJour.map((t) => (
                <Link
                  key={t.id}
                  to="/taches/$id"
                  params={{ id: t.id }}
                  className={`semaine-item semaine-tache ${t.enRetard ? 'en-retard' : ''}`}
                >
                  <strong>{t.titre}</strong>
                  <span className="discret">
                    {[t.entite, t.executant ?? t.executantTiers ?? 'à prendre']
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </Link>
              ))}
              {vide && <span className="semaine-rien">Rien</span>}
            </section>
          )
        })}
      </div>

      <ul className="frise-legende" aria-label="Légende">
        <li>
          <span className="semaine-legende" /> échéance
        </li>
        <li>
          <span className="semaine-legende a-confirmer" /> à confirmer
        </li>
        <li>
          <span className="pastille pastille-retard" /> paiement en retard
        </li>
        {paiements.length > 0 && (
          <li>
            <span className="semaine-legende semaine-paiement" /> paiement attendu
          </li>
        )}
        {taches.length > 0 && (
          <li>
            <span className="semaine-legende semaine-tache" /> tâche
          </li>
        )}
      </ul>
    </div>
  )
}

function EnteteJour({
  jour,
  aujourdhui,
  telephone = false,
}: {
  jour: string
  aujourdhui: string
  telephone?: boolean
}) {
  const nom = JOURS[jourDeLaSemaine(jour)]!
  return (
    <div
      className={[
        'semaine-jour-titre',
        telephone ? 'telephone' : '',
        jour === aujourdhui ? 'aujourdhui' : '',
      ].join(' ')}
    >
      <span className="semaine-jour-nom">{telephone ? nom : `${nom.slice(0, 3)}.`}</span>
      <span className="semaine-jour-numero">{Number(jour.slice(8, 10))}</span>
      {jour === aujourdhui && <span className="semaine-jour-nom">aujourd’hui</span>}
    </div>
  )
}
